import InsightsIcon from '@mui/icons-material/Insights';
import { Box, Button, Chip, Divider, Fade, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { getChainId } from '../../../../lib';

interface SimulationData {
  totalCustomers: number;
  matchingCustomers: number;
  perGroupResults: Array<{
    groupId: number;
    matchingCustomers: number;
    rules: Array<{
      ruleIndex: number;
      matchingCustomers: number;
      simulatable: boolean;
    }>;
  }>;
  period: { days: number };
}

interface GiftRuleSimulatorProps {
  type: string;
  fuelIds: string[];
  fuelNames?: string[];
  // Aceita 'points' só para não obrigar quem chama a estreitar o tipo: a regra
  // points_exchange nunca é simulada (SIMULATABLE_TYPES é só fillin /
  // accumulated_fillin) e o componente sequer é renderizado para ela.
  valueType: 'volume' | 'cents' | 'points';
  value: number;
  quantity: number;
  stationIds: string[];
}

const shimmerKeyframes = {
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
};

const GiftRuleSimulator: React.FC<GiftRuleSimulatorProps> = ({
  type, fuelIds, fuelNames, valueType, value, quantity, stationIds,
}) => {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SimulationData | null>(null);
  const [days, setDays] = useState(30);
  const [hasFetched, setHasFetched] = useState(false);

  const canSimulate = useMemo(() => {
    if (type !== 'fillin' && type !== 'accumulated_fillin') return false;
    if (!stationIds || stationIds.length === 0) return false;
    return true;
  }, [type, stationIds]);

  const handleFetch = useCallback(async () => {
    if (!canSimulate) return;
    setLoading(true);
    try {
      const chainId = getChainId();
      const rules = JSON.stringify([{
        type,
        fuelIds,
        valueType,
        value,
        quantity: quantity || 1,
        stationIds,
        groupId: 0,
      }]);

      const response = await dataProvider.getList('gifts/simulate-rules', {
        filter: { chainId, days, rules },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      });

      const result = response.data?.[0] as any;
      if (result) {
        setData(result);
        setHasFetched(true);
      }
    } catch (e) {
      console.error('Rule simulation error:', e);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, type, fuelIds, valueType, value, quantity, stationIds, days, canSimulate]);

  const showResults = data && hasFetched && !loading;

  // Pegar resultado da regra individual (primeira regra do primeiro grupo)
  const ruleResult = useMemo(() => {
    if (!data) return null;
    const firstGroup = data.perGroupResults?.[0];
    const firstRule = firstGroup?.rules?.[0];
    if (!firstRule?.simulatable) return null;
    return { matching: firstRule.matchingCustomers, total: data.totalCustomers };
  }, [data]);

  if (!canSimulate) return null;

  return (
    <Box
      sx={{
        border: '1px solid rgba(102, 126, 234, 0.15)',
        borderRadius: '12px',
        background: 'rgba(102, 126, 234, 0.03)',
        p: 2,
        mt: 1,
        ...shimmerKeyframes,
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <InsightsIcon sx={{ color: '#667eea', fontSize: '1.1rem' }} />
        <Typography sx={{ fontSize: '0.83rem', color: '#667eea', fontWeight: 600, mr: 0.5 }}>
          Simulação de Alcance
        </Typography>

        <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
          {fuelNames && fuelNames.length > 0 ? (
            fuelNames.map((name, i) => (
              <Chip
                key={i}
                label={name}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
              />
            ))
          ) : (
            <Chip
              label="Todos os combustíveis"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
            />
          )}
          <Chip
            label={`${stationIds.length} posto${stationIds.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea', fontWeight: 500 }}
          />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: 'rgba(102,126,234,0.2)' }} />

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>últimos</Typography>
          <input
            type="number"
            min={7}
            max={180}
            value={days}
            onChange={e => setDays(Math.min(180, Math.max(7, Number(e.target.value) || 30)))}
            style={{
              width: 36,
              textAlign: 'center',
              border: 'none',
              borderBottom: '1px solid rgba(102,126,234,0.4)',
              background: 'transparent',
              color: '#667eea',
              fontWeight: 700,
              fontSize: '0.76rem',
              outline: 'none',
              padding: '0 2px',
            }}
          />
          <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>dias</Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<InsightsIcon sx={{ fontSize: '0.9rem !important' }} />}
          onClick={handleFetch}
          disabled={loading}
          sx={{
            ml: 'auto',
            borderColor: '#667eea',
            color: '#667eea',
            fontSize: '0.76rem',
            textTransform: 'none',
            py: 0.3,
            px: 1.5,
            '&:hover': { background: 'rgba(102,126,234,0.08)', borderColor: '#5a6fd6' },
          }}
        >
          {showResults ? 'Atualizar' : 'Analisar histórico'}
        </Button>
      </Box>

      {/* Loading shimmer */}
      {loading && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(102,126,234,0.6)', mb: 1, fontStyle: 'italic' }}>
            Analisando abastecimentos de {stationIds.length} posto{stationIds.length !== 1 ? 's' : ''} nos últimos {days} dias...
          </Typography>
          {[1, 2, 3].map(i => (
            <Box
              key={i}
              sx={{
                height: 32,
                borderRadius: '8px',
                mb: 0.8,
                background: 'linear-gradient(90deg, rgba(102,126,234,0.06) 25%, rgba(118,75,162,0.12) 50%, rgba(102,126,234,0.06) 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Results */}
      {showResults && ruleResult && (
        <Fade in timeout={400}>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 1.2 }}>
              Análise baseada em <b>{ruleResult.total} cliente{ruleResult.total !== 1 ? 's' : ''}</b> que abasteceram · últimos <b>{days} dias</b>
            </Typography>

            {ruleResult.total === 0 ? (
              <Box sx={{ py: 1.5, px: 1.5, borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  Nenhum cliente encontrado para os postos e período selecionados.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Simulation count */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    bgcolor: ruleResult.matching === 0 ? 'rgba(198,40,40,0.06)' : ruleResult.matching === ruleResult.total ? 'rgba(46,125,50,0.06)' : 'rgba(102,126,234,0.06)',
                    border: `1px solid ${ruleResult.matching === 0 ? 'rgba(198,40,40,0.15)' : ruleResult.matching === ruleResult.total ? 'rgba(46,125,50,0.15)' : 'rgba(102,126,234,0.12)'}`,
                  }}
                >
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.88rem', color: ruleResult.matching === 0 ? '#c62828' : ruleResult.matching === ruleResult.total ? '#2e7d32' : '#667eea' }}>
                      {ruleResult.matching} de {ruleResult.total}
                    </Typography>
                    {' '}cliente{ruleResult.total !== 1 ? 's' : ''} teriam atingido esta regra
                  </Typography>
                </Box>

                {/* Progress bar */}
                <Box sx={{ mt: 1.2 }}>
                  <Box sx={{ position: 'relative', height: 10, borderRadius: 99, overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.06)' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${(ruleResult.matching / ruleResult.total) * 100}%`,
                        borderRadius: 99,
                        background: ruleResult.matching === ruleResult.total
                          ? 'linear-gradient(90deg, #66bb6a, #43a047)'
                          : ruleResult.matching === 0
                            ? 'linear-gradient(90deg, #ef5350, #e53935)'
                            : 'linear-gradient(90deg, #667eea, #764ba2)',
                        transition: 'width 0.4s ease',
                        boxShadow: ruleResult.matching > 0 ? '0 0 8px rgba(102,126,234,0.3)' : 'none',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                      {Math.round((ruleResult.matching / ruleResult.total) * 100)}% atingiriam
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                      {ruleResult.matching}/{ruleResult.total}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default GiftRuleSimulator;
